import React, { useMemo, useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {Wallet} from "lucide-react"

import {
  DesktopTableParts,
  MobileCardsOnly,
} from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
} from "components/DataTableLoader";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { UIReactSelect } from "../../components/Inputfields";
import { showSuccess, showError } from "toast/toast";

// API calls
import {
  createTopupPaymentIntent,
  getWalletBalance,
  fetchWalletTransactions,
  WalletBalanceResponse,
  WalletTransaction,
} from "./Shipmentsapicalls";

// Stripe Payment Form Component
import StripePaymentForm from "components/Shipements/StripePaymentForm";

/* ================= STATIC OPTIONS ================= */

import { getAuthCookie } from "../../utils/authCookies";

const ADD_AMOUNT_OPTIONS = [
  { value: "500", label: "$500" },
  { value: "1000", label: "$1000" },
  { value: "2500", label: "$2500" },
  { value: "3500", label: "$3500" },
];

/* ================= COLUMN HELPER ================= */

const columnHelper = createColumnHelper<WalletTransaction>();

/* ================= HELPER FUNCTION ================= */

const formatDate = (dateString: string) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTransactionAmount = (amount: string, type: string) => {
  const value = Math.abs(Number(amount) || 0);
  const formatted = Number.isFinite(value) ? value.toFixed(2) : amount;
  const isDebit = String(type || "").trim().toLowerCase() === "debit";
  return { text: `${isDebit ? "-" : "+"}$${formatted}`, isDebit };
};

const WALLET_STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  succeeded: { bg: "#d2f5d2", color: "#2a6b2a", label: "Succeeded" },
  pending: { bg: "#ffe0b3", color: "#b76b00", label: "Pending" },
  failed: { bg: "#fddede", color: "#d93025", label: "Failed" },
};

const getStatusBadge = (status: string) => {
  const key = String(status || "").trim().toLowerCase();
  const cfg = WALLET_STATUS_COLORS[key] || {
    bg: "#e5e7eb",
    color: "#4b5563",
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "—",
  };

  return (
    <div
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: "0.525rem",
        fontSize: "0.8125rem",
        textAlign: "center",
        backgroundColor: cfg.bg,
        color: cfg.color,
        border: "0.2px solid",
        borderColor: `${cfg.color}50`,
      }}
    >
      {cfg.label}
    </div>
  );
};

/* ================================================= */

const PostageWallet = () => {
  const token = getAuthCookie("sessionToken") || "";

  /* ================= STATE ================= */
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [loadingPayment, setLoadingPayment] = useState(false);

  // Wallet balance state
  const [walletBalance, setWalletBalance] = useState<WalletBalanceResponse | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Transactions state
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const fetchRequestIdRef = useRef(0);
  const hasLoadedTransactionsRef = useRef(false);

  /* ================= FETCH WALLET BALANCE ON MOUNT ================= */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get("redirect_status");
    if (redirectStatus === "succeeded") {
      showSuccess("Funds added successfully!");
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
      return;
    }

    const fetchBalance = async () => {
      setLoadingBalance(true);
      try {
        const data = await getWalletBalance(token);
        setWalletBalance(data);
      } catch (err) {
        console.error("Failed to fetch wallet balance:", err);
      } finally {
        setLoadingBalance(false);
      }
    };

    fetchBalance();
  }, [token]);

  /* ================= FETCH TRANSACTIONS ================= */
  useEffect(() => {
    const fetchTransactions = async () => {
      const requestId = ++fetchRequestIdRef.current;
      const showInitialLoader = !hasLoadedTransactionsRef.current;
      if (showInitialLoader) setLoadingTransactions(true);
      try {
        const result = await fetchWalletTransactions(token, {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: "",
          filters: {},
          sort: { field: "created_at", order: "desc" },
        });

        if (requestId !== fetchRequestIdRef.current) return;
        setTransactions(result.data || []);
        setRowCount(result.total || 0);
        hasLoadedTransactionsRef.current = true;
      } catch (err) {
        console.error("Failed to fetch transactions:", err);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoadingTransactions(false);
        }
      }
    };

    fetchTransactions();
  }, [token, pagination.pageIndex, pagination.pageSize]);

  /* ================= BALANCE STYLING (Red if < $100, Green otherwise) ================= */
  const balanceAmount = Number(walletBalance?.balance || 0);
  const isLowBalance = balanceAmount < 500;

  const balanceCardStyle = {
    background: isLowBalance ? "#ffebee" : "#dcfce7",
  };

  /* ================= FORMIK ================= */
  const formik = useFormik({
    initialValues: {
      amount: "",
    },
    onSubmit: () => {},
  });

  /* ================= FETCH PAYMENT INTENT WHEN AMOUNT CHANGES ================= */
  useEffect(() => {
    const fetchPaymentIntent = async () => {
      const selectedAmount = Number(formik.values.amount);

      if (!selectedAmount) {
        setClientSecret("");
        setStripePromise(null);
        return;
      }

      setLoadingPayment(true);

      try {
        const data = await createTopupPaymentIntent(token, selectedAmount);
        setClientSecret(data.client_secret);

        if (data.publishable_key) {
          const promise = loadStripe(data.publishable_key);
          setStripePromise(promise);
        }
      } catch (err) {
        console.error("Failed to create payment intent:", err);
        showError(err instanceof Error ? err.message : "Failed to initialize payment");
        setClientSecret("");
        setStripePromise(null);
      } finally {
        setLoadingPayment(false);
      }
    };

    fetchPaymentIntent();
  }, [formik.values.amount, token]);

  /* ================= PAYMENT SUCCESS HANDLER ================= */
  const handlePaymentSuccess = () => {
    showSuccess("Funds added successfully!");
    setTimeout(() => {
      window.location.reload();
    }, 400);
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = useMemo(
    () => [
      // columnHelper.display({
      //   id: "sno",
      //   header: () => <div className="th-text text-center">S.No.</div>,
      //   cell: (info) => (
      //     <div className="text-center">
      //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
      //     </div>
      //   ),
      // }),
      columnHelper.accessor("created_at", {
        header: () => <div className="th-text">TRANSACTION DATE</div>,
        cell: (info) => <div>{formatDate(info.getValue())}</div>,
      }),
      columnHelper.accessor("starting_balance", {
        header: () => <div className="th-text">STARTING BALANCE</div>,
        cell: (info) => <div>${info.getValue()}</div>,
      }),
      columnHelper.accessor("type", {
        header: () => <div className="th-text">TYPE</div>,
        cell: (info) => (
          <div className="text-capitalize">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("amount", {
        header: () => <div className="th-text">Amount</div>,
        cell: (info) => {
          const { text, isDebit } = formatTransactionAmount(
            info.getValue(),
            info.row.original.type
          );
          return (
            <div
              className={`text-sm S-No ${isDebit ? "" : "order-link-1"}`}
              style={isDebit ? { color: "#d93025", fontWeight: 400 } : undefined}
            >
              {text}
            </div>
          );
        },
      }),
      columnHelper.accessor("ending_balance", {
        header: () => <div className="th-text">ENDING BALANCE</div>,
        cell: (info) => <div>${info.getValue()}</div>,
      }),
      columnHelper.accessor("status", {
        header: () => <div className="th-text text-center">Status</div>,
        cell: (info) => (
          <div className="text-sm text-center">{getStatusBadge(info.getValue())}</div>
        ),
      }),
    ],
    [pagination]
  );

  /* ================= TABLE INSTANCE ================= */
  const table = useReactTable({
    data: transactions,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: { pagination },
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  const showTableLoader = loadingTransactions && transactions.length === 0;

  return (
    <div className="content container">

      {/* ================= HEADER ================= */}
      <div className="card box-shadow-datatables shadow-border-2 mb-4">
        <div className="card-body d-flex align-items-center gap-3 py-4">
          <div
            className="text-primary br-8 d-flex align-items-center justify-content-center"
            style={{ width: 40, height: 40, backgroundColor: "#dbeafe" }}
          >
            <Wallet className="fs-5" style={{ color: "#155dfc" }} />
          </div>
          <h3 className="mb-0 fw-bold">Postage Wallet</h3>
        </div>
      </div>

      {/* ================= BALANCE ================= */}
      <div
        className="card box-shadow-datatables shadow-border-2 mb-4 text-center"
        style={balanceCardStyle}
      >
        <div className="card-body py-4">
          <div className="fs-18 fw-medium mb-3 text-muted">Current Balance</div>
          {loadingBalance ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <h1 className={`fw-bold mb-3`} style={{ color: isLowBalance ? "#ef4444" : "#008236" }}>
              ${walletBalance?.balance || "0.00"}
            </h1>
          )}
          <div className="text-muted fs-16">Available for shipping labels</div>
          {isLowBalance && !loadingBalance && (
            <div className="text-danger mt-2 fs-15">
              <i className="ri-error-warning-line me-1" />
              Low balance! Please add funds.
            </div>
          )}
        </div>
      </div>

      {/* ================= ADD FUNDS ================= */}
      <div className="card box-shadow-datatables shadow-border-2 mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-4">
            <i className="ri-add-line fs-4" />
            <h3 className="mb-0 fw-bold">Add Funds</h3>
          </div>

          <p className="text-muted fs-16 fw-medium mb-4">
            Select an amount to add to your postage wallet
          </p>

          <div
  className="row g-3 align-items-start"
  style={{ position: "relative", zIndex: 10, overflow: "visible" }}
>
  {/* Amount - COL-3 */}
  <div className="col-lg-3 col-md-4">
    <UIReactSelect
      formik={formik}
      name="amount"
      label="Amount"
      options={ADD_AMOUNT_OPTIONS}
      placeholder="Select amount to add"
    />
  </div>
  <div className="col-lg-9 col-md-8"></div>

  {/* Stripe Payment Element - COL-9 */}
  <div className="col-lg-6 col-md-5">
  {/* Loading State */}
          {loadingPayment && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Initializing payment...</p>
            </div>
          )}

          {/* Stripe Payment Element */}
          {!loadingPayment && stripePromise && clientSecret && (
            <div className="mt-4">
              <Elements
                key={clientSecret}
                stripe={stripePromise}
                options={{ clientSecret }}
              >
                <StripePaymentForm
                  amount={Number(formik.values.amount)}
                  onSuccess={handlePaymentSuccess}
                />
              </Elements>
            </div>
          )}
  </div>
</div>


          {/* Amount Selection */}
          {/* <div className="row">
            <div className="col-md-6">
              <Select
                formik={formik}
                name="amount"
                label="Amount"
                options={ADD_AMOUNT_OPTIONS}
                placeholder="Select amount to add"
              />
            </div>
          </div>
          {loadingPayment && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Initializing payment...</p>
            </div>
          )}
          {!loadingPayment && stripePromise && clientSecret && (
            <div className="mt-4">
              <Elements
                key={clientSecret}
                stripe={stripePromise}
                options={{ clientSecret }}
              >
                <StripePaymentForm
                  amount={Number(formik.values.amount)}
                  onSuccess={handlePaymentSuccess}
                />
              </Elements>
            </div>
          )} */}
        </div>
      </div>

      {/* ================= RECENT TRANSACTIONS ================= */}
      <div className="card box-shadow-datatables shadow-border-2 mb-4">
        <div className="card-body">
          <h5 className="fw-semibold mb-3">Recent Transactions</h5>

          {showTableLoader ? (
            <>
              <div className="rdt-desktop">
                <div className="production-scroll">
                  <table className="table production-table align-middle mb-0">
                    <DesktopTableLoader table={table} message="Loading transactions…" />
                  </table>
                </div>
              </div>

              <div className="rdt-mobile">
                <MobileCardsLoader message="Loading transactions…" />
              </div>
            </>
          ) : transactions.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="ri-inbox-line fs-1 d-block mb-2" />
              No transactions yet. Add funds to get started!
            </div>
          ) : (
            <>
              <div className="rdt-desktop">
                <div className="production-scroll">
                  <table className="table production-table align-middle mb-0">
                    <DesktopTableParts table={table} />
                  </table>
                </div>
              </div>

              <div className="rdt-mobile">
                <MobileCardsOnly
                  table={table}
                  titleColId="created_at"
                  actionColId="status"
                />
              </div>

              <div className="mt-4">
                <TablePaginationControls table={table} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostageWallet;
